import type { AIModelChoices, Annotation, AnnotationFeedbackDetail } from 'api';
import { useTranslation } from 'i18n';
import { logger } from 'monitor';
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CenteredLoader, useBreakpoint } from 'ui';
import { useToggle } from '../../../disclosure/hooks/useToggle/useToggle';

import { useSegmentationComments } from '../../hooks/useSegmentationComments/useSegmentationComments';
import { useSegmentationOverlay } from '../../hooks/useSegmentationOverlay/useSegmentationOverlay';
import { useShowAiFeedback } from '../../hooks/useShowAiFeedback/useShowAiFeedback';
import { useTouchHandlers } from '../../hooks/useTouchHandlers/useTouchHandlers';
import { type MobileZoom, reverseTransformPoint, transformPoint } from '../../utils/canvas/canvas';
import { CommentFormDrawer, CommentFormValues } from '../CommentFormDrawer/CommentFormDrawer';
import { COMMENT_SIZE, CommentMarker } from '../CommentMarker/CommentMarker';
import { HelpAndFeedbackBar } from '../HelpAndFeedbackBar/HelpAndFeedbackBar';
import { ZoomOverlay } from '../ZoomOverlay/ZoomOverlay';
import { Canvas, CanvasContainer, LoaderContainer, PositioningBase } from './styles';

export type PendingComment = Pick<AnnotationFeedbackDetail, 'x_coordinate' | 'y_coordinate' | 'id'>;

const AVATAR_MARGINS = {
  bottom: 0,
  left: -(COMMENT_SIZE.width * 0.25),
  right: -(COMMENT_SIZE.width * 0.75),
  top: -COMMENT_SIZE.height,
};

export interface ScaledComment {
  comment: AnnotationFeedbackDetail | PendingComment;
  x: number;
  y: number;
  isVisible?: boolean;
}

export const SegmentationOverlay = ({
  imageSrc,
  annotations,
  hiddenCategories,
  model,
  showLegend = true,
  showAerosols,
  showFeedback,
  evidenceId,
}: {
  imageSrc: string;
  annotations: Annotation[];
  hiddenCategories: readonly string[];
  model: AIModelChoices;
  showLegend?: boolean;
  showAerosols: boolean;
  showFeedback?: boolean;
  evidenceId: number;
}) => {
  const { t } = useTranslation('features');
  const { isMobile } = useBreakpoint();
  const { open, isOpen, close } = useToggle();
  const { showAiFeedback } = useShowAiFeedback();

  const isFeedbackFeatureEnabled = Boolean(showFeedback);
  const shouldFetchComments = isFeedbackFeatureEnabled && showAiFeedback;
  const { createComment, deleteComment, comments, updateComment, isCommentsLoading } = useSegmentationComments(
    evidenceId,
    {
      enableFetch: shouldFetchComments,
    }
  );

  const [pendingComment, setPendingComment] = useState<PendingComment | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);
  const [editingComment, setEditingComment] = useState<AnnotationFeedbackDetail | null>(null);
  const [canvasElement, setCanvasElement] = useState<HTMLCanvasElement | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scaleRef = useRef<number | null>(null);
  const mobileZoomRef = useRef<MobileZoom>({ isZooming: false, scale: 1, translateX: 0, translateY: 0 });

  const handleCanvasRef = useCallback((element: HTMLCanvasElement | null) => {
    canvasRef.current = element;
    setCanvasElement(element);
  }, []);

  const createCommentAtPosition = useCallback(
    (clientX: number, clientY: number) => {
      const scale = scaleRef.current;
      const mobileZoom = mobileZoomRef.current;

      if (!canvasElement || scale == null || pendingComment || isCommentsLoading || !isFeedbackFeatureEnabled) {
        logger.info('Cannot create comment', {
          pendingComment,
          scale,
          showFeedback,
          showLoader: isCommentsLoading,
        });
        return;
      }

      const canvas = canvasElement;
      const rect = canvas.getBoundingClientRect();
      const clickX = clientX - rect.left;
      const clickY = clientY - rect.top;

      const reversed = reverseTransformPoint(clickX, clickY, canvas.clientWidth, canvas.clientHeight, mobileZoom);

      const newComment: PendingComment = {
        id: Date.now(),
        x_coordinate: reversed.x / scale,
        y_coordinate: reversed.y / scale,
      };

      setPendingComment(newComment);
      open();
    },
    [canvasElement, pendingComment, isCommentsLoading, isFeedbackFeatureEnabled, open, showFeedback]
  );

  const { mobileZoom, setMobileZoom } = useTouchHandlers({
    canvasRef,
    isMobile,
    onLongPress: isMobile && isFeedbackFeatureEnabled ? createCommentAtPosition : undefined,
  });

  useEffect(() => {
    setMobileZoom({ isZooming: false, scale: 1, translateX: 0, translateY: 0 });
  }, [imageSrc, setMobileZoom]);

  const { canvasRect, isLoading, isZoomVisible, mousePosition, visibleAnnotations, labelColourMap, scale } =
    useSegmentationOverlay({
      annotations,
      canvasRef,
      hiddenCategories,
      imageSrc,
      isMobile,
      mobileZoom,
      model,
      showAerosols,
      showLegend,
    });

  useEffect(() => {
    mobileZoomRef.current = mobileZoom;
  }, [mobileZoom]);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  const helpText = isMobile
    ? t('annotations.segmentationOverlay.helpText.mobile')
    : t('annotations.segmentationOverlay.helpText.desktop');

  const allComments = useMemo(
    () => [...comments, ...(pendingComment ? [pendingComment] : [])],
    [comments, pendingComment]
  );

  const scaledComments: ScaledComment[] = useMemo(() => {
    if (!scale || !canvasElement) return [];

    const canvasWidth = canvasElement.clientWidth;
    const canvasHeight = canvasElement.clientHeight;

    return allComments
      .map((comment) => {
        const scaledX = comment.x_coordinate * scale;
        const scaledY = comment.y_coordinate * scale;

        const transformed = transformPoint(scaledX, scaledY, canvasWidth, canvasHeight, mobileZoom, AVATAR_MARGINS);

        return {
          comment,
          isVisible: transformed.isVisible,
          x: transformed.x,
          y: transformed.y,
        };
      })
      .filter((c) => c.isVisible);
  }, [scale, canvasElement, allComments, mobileZoom]);

  const handleDrawerClose = useCallback(() => {
    setPendingComment(null);
    setEditingComment(null);
    close();
  }, [close]);

  const handleCommentSave = useCallback(
    (data: CommentFormValues) => {
      if (editingComment) {
        updateComment({
          data: {
            ...data,
            x_coordinate: Math.round(editingComment.x_coordinate),
            y_coordinate: Math.round(editingComment.y_coordinate),
          },
          feedbackId: editingComment.id,
        });
      } else {
        createComment({
          data: {
            ...data,
            x_coordinate: Math.round(pendingComment?.x_coordinate ?? 0),
            y_coordinate: Math.round(pendingComment?.y_coordinate ?? 0),
          },
          evidenceId,
        });
      }

      setPendingComment(null);
      setEditingComment(null);
      setSelectedCommentId(null);
      close();
    },
    [
      editingComment,
      close,
      updateComment,
      pendingComment?.x_coordinate,
      pendingComment?.y_coordinate,
      createComment,
      evidenceId,
    ]
  );

  const handleCanvasContextMenu = useCallback(
    (e: MouseEvent<HTMLCanvasElement>) => {
      if (!isFeedbackFeatureEnabled) {
        return;
      }
      e.preventDefault();
      createCommentAtPosition(e.clientX, e.clientY);
    },
    [isFeedbackFeatureEnabled, createCommentAtPosition]
  );

  const editExistingComment = (comment: AnnotationFeedbackDetail): void => {
    setEditingComment(comment);
    setSelectedCommentId(null);
    open();
  };

  const shouldShowZoom = isZoomVisible && !isMobile;
  const shouldShowDrawer = Boolean(pendingComment || editingComment);

  const isDesktopFeedbackEnabled = isFeedbackFeatureEnabled && !isMobile;
  return (
    <div>
      {isCommentsLoading || isLoading ? (
        <LoaderContainer>
          <CenteredLoader />
        </LoaderContainer>
      ) : null}
      <HelpAndFeedbackBar helpText={helpText} isAiFeedbackEnabled={isFeedbackFeatureEnabled} imageSrc={imageSrc} />

      <CanvasContainer>
        <PositioningBase>
          <Canvas
            giveFeedback={isDesktopFeedbackEnabled}
            ref={handleCanvasRef}
            isLoading={isLoading}
            aria-label={t('annotations.segmentationOverlay.ariaLabel')}
            data-testid="segmentation-overlay"
            onContextMenu={handleCanvasContextMenu}
          />
          {scaledComments.map((scaledComment) => (
            <CommentMarker
              key={scaledComment.comment.id}
              scaledComment={scaledComment}
              isSelected={selectedCommentId === scaledComment.comment.id}
              onSelect={setSelectedCommentId}
              onEdit={editExistingComment}
              onDelete={(id) => deleteComment({ feedbackId: id })}
              onPendingClick={open}
            />
          ))}
        </PositioningBase>
      </CanvasContainer>

      {shouldShowZoom ? (
        <ZoomOverlay
          isVisible={isZoomVisible}
          mousePosition={mousePosition}
          sourceCanvas={canvasElement}
          sourceRect={canvasRect}
          imageSrc={imageSrc}
          annotations={visibleAnnotations}
          hiddenCategories={hiddenCategories}
          labelColourMap={labelColourMap}
        />
      ) : null}

      {shouldShowDrawer ? (
        <CommentFormDrawer
          isOpen={isOpen}
          onClose={handleDrawerClose}
          onSave={handleCommentSave}
          comment={
            editingComment
              ? {
                  detail: editingComment.detail,
                  feedback_type: editingComment.feedback_type,
                  id: editingComment.id,
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
};
